import { useIsAuthenticated } from '@interhyp/platform-auth';
import { useHistory } from '@interhyp/platform-router';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * This function listen on the router for the activation page.
 * After the user leaves the activation page the intents query will be invalidated (triggers a refetch).
 * This happens after a timeout because the backend has to be synced (at the moment we have no option to detect a successful sync)
 * @param triggerRefetch - flag if a refetch should be triggered
 */
export function useInvalidateIntentsAfterActivation(triggerRefetch: boolean): {
  openActivationPage: boolean;
  waitForFetch: boolean;
  reset: () => void;
} {
  const client = useQueryClient();
  const isAuthenticated = useIsAuthenticated();

  // This helper var will prevent redirecting until the latest intents has been fetched (after activation)
  const [openActivationPage, setOpenActivationPage] = useState<boolean>(false);
  // This helper var says if the user is currently waiting until the next intent fetch will be triggered
  // => waiting is required because otherwise old data may be loaded from the backend
  const [waitForFetch, setWaitForFetch] = useState<boolean>(false);

  const routerHistory = useHistory();
  useEffect(() => {
    let timeout: number | undefined;

    return routerHistory.listen(({ pathname }) => {
      window.clearTimeout(timeout);

      if (pathname.includes('/login/activation')) {
        setOpenActivationPage(true);
        return;
      }

      if (!openActivationPage) {
        return;
      }

      if (!triggerRefetch) {
        setOpenActivationPage(false);
        return;
      }

      setWaitForFetch(true);
      // Reset with delay to give the activation backend task more time to sync
      timeout = window.setTimeout(() => {
        // Reset intents after user leaves activation page
        void client.invalidateQueries({ queryKey: ['intents', true] });
      }, 5000);

      return () => window.clearTimeout(timeout);
    });
  }, [routerHistory, client, isAuthenticated, openActivationPage, triggerRefetch]);

  const reset = useCallback(() => {
    setOpenActivationPage(false);
    setWaitForFetch(false);
  }, []);

  return useMemo(
    () => ({
      openActivationPage,
      waitForFetch,
      reset,
    }),
    [openActivationPage, waitForFetch, reset],
  );
}
